import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { RadiationMachineService } from './radiation-machine.service';
import { TransactionService } from 'src/app/pages/transaction/services/transaction.service';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { DynamicFormService } from 'src/app/core/services/dynamic-form.service';
import { MatSnackBar } from '@angular/material';
import { ProfileService } from 'src/app/pages/account/services/profile.service';
import { TransactionRequirementService } from 'src/app/pages/transaction/services/transaction-requirement.service';
import { TransactionRequirementItemServiceService } from 'src/app/pages/transaction/services/transaction-requirement-item.service';
import { LsHelperService } from 'src/app/core/services/ls-helper.service';

@Component({
  selector: 'prgov-radiation-machine-form',
  templateUrl: './radiation-machine-form.component.html',
  styleUrls: ['./radiation-machine-form.component.scss']
})
export class RadiationMachineFormComponent implements OnInit {
  radiationForm: FormGroup;
  isLoading = false;
  isSaving = false;
  machineTypes: any[] = [];
  purposeTypes: any[] = [];
  machineList: any[] = [];
  showMachineForm: boolean = false;
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  currentMachineId: number | null = null;
  otherPurpose: boolean = false;
  otherType: boolean = false;
  profileId: number | null = null;
  canEdit: boolean = false;

  displayedColumns: string[] = [
    'Tipo',
    'Proposito',
    'KvP',
    'mA',
    'Fija',
    'Movil',
    'Manufacturero y Nro. Modelo',
    'Nro. de serie',
    'Acciones'
  ];
  dataSource: MatTableDataSource<any>;

  constructor(
    private fb: FormBuilder,
    private radiationMachineService: RadiationMachineService,
    public transactionService: TransactionService,
    public dynamicFormService: DynamicFormService,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private profileService: ProfileService,
    public transactionRequirementService: TransactionRequirementItemServiceService,
    private ls: LsHelperService
  ) {
    this.createForm();
    this.dynamicFormService.formCommunicator.subscribe((data) => {
      if (data === 'save-data') {
        this.save();
      }
    });
  }

  ngOnInit() {
    this.loadInitialData();
    this.profileId = this.ls.get('currentProfileId');
    if(this.profileId){
      this.profileService.getById(this.profileId).then((data: any) => {
        this.radiationForm.patchValue({
          ownerName: data.Name + ' ' + data.LastName,
          ownerAddress: data.OtherStreet1,
          ownerPhone: data.Phone1
        });
      });
    }
    this.canEdit = this.transactionService.itemToEdit.CurrentStatus === 'Borrador' ||
                   this.transactionRequirementService.requirementItem.IsFlagedForVerification;
  }

  loadInitialData() {
    this.radiationMachineService.getMachineTypes().subscribe((data: any) => {
      this.machineTypes = data.Data;
    });
    this.radiationMachineService.getMachinePurposes().subscribe((data: any) => {
      this.purposeTypes = data.Data;
    });
    this.loadMachineList();
  }

  loadMachineList() {
    this.radiationMachineService.getMachineById(this.dynamicFormService.requirement.UserProcessRequirementId).subscribe((data: any) => {
      this.machineList = data.Data;
      this.dataSource = new MatTableDataSource<any>(this.machineList);
    });
  }

  onChangeType(event: any) {
    const value = event.value;
    const machineTypeOtherControl = this.radiationForm.get('machineTypeOther');

    if (value === 'c530f4b6-ad8e-4585-9394-dbaaca98656a') {
      this.otherType = true;
      if (machineTypeOtherControl) {
        machineTypeOtherControl.setValidators([Validators.required]);
        machineTypeOtherControl.updateValueAndValidity();
      }
    } else {
      this.otherType = false;
      if (machineTypeOtherControl) {
        machineTypeOtherControl.clearValidators();
        machineTypeOtherControl.updateValueAndValidity();
      }
    }
  }

  onChangeMachinePurpose(event: any) {
    const value = event.value;
    const machinePurposeOtherControl = this.radiationForm.get('machinePurposeOther');

    if (value === '84be5006-c3d1-4a82-9c6d-8ee1d64dd298') {
      this.otherPurpose = true;
      if (machinePurposeOtherControl) {
        machinePurposeOtherControl.setValidators([Validators.required]);
        machinePurposeOtherControl.updateValueAndValidity();
      }
    } else {
      this.otherPurpose = false;
      if (machinePurposeOtherControl) {
        machinePurposeOtherControl.clearValidators();
        machinePurposeOtherControl.updateValueAndValidity();
      }
    }
  }

  createForm() {
    this.radiationForm = this.fb.group({
      userProcessRequirementId: [this.dynamicFormService.requirement.UserProcessRequirementId],
      ownerName: ['', Validators.required],
      ownerAddress: ['', Validators.required],
      ownerPhone: ['', Validators.required],
      machineTypeId: ['', Validators.required],
      machineTypeOther: [''],
      machinePurposeId: ['', Validators.required],
      machinePurposeOther: [''],
      kvp: ['', [Validators.min(0)]],
      mA: ['', [Validators.min(0)]],
      fixed: ['No', Validators.required],
      mobile: ['No', Validators.required],
      manufacturerAndModel: ['', Validators.required],
      serialNumber: ['', Validators.required],
      machineLocation: [''] // Campo opcional
    });
  }

  editMachine(machine: any) {
    if (!this.canEdit) {
      this.matSnackBar.open('No se puede editar en el estado actual de la transacción', 'OK', {
        duration: 3000
      });
      return;
    }

    this.isEditMode = true;
    this.isViewMode = false;
    this.currentMachineId = machine.Id;
    this.showMachineForm = true;

    this.otherType = machine.MachineTypeId === 'c530f4b6-ad8e-4585-9394-dbaaca98656a';
    this.otherPurpose = machine.MachinePurposeId === '84be5006-c3d1-4a82-9c6d-8ee1d64dd298';

    this.radiationForm.patchValue({
      ownerName: machine.OwnerName,
      ownerAddress: machine.OwnerAddress,
      ownerPhone: machine.OwnerPhone,
      machineTypeId: machine.MachineTypeId,
      machineTypeOther: machine.MachineTypeOther,
      machinePurposeId: machine.MachinePurposeId,
      machinePurposeOther: machine.MachinePurposeOther,
      kvp: machine.Kvp,
      mA: machine.mA,
      fixed: machine.Fixed,
      mobile: machine.Mobile,
      manufacturerAndModel: machine.ManufacturerAndModel,
      serialNumber: machine.SerialNumber || '',
      machineLocation: machine.MachineLocation || ''
    });
  }

  viewMachine(machine: any) {
    this.isEditMode = false;
    this.isViewMode = true;
    this.currentMachineId = machine.Id;
    this.showMachineForm = true;

    this.otherType = machine.MachineTypeId === 'c530f4b6-ad8e-4585-9394-dbaaca98656a';
    this.otherPurpose = machine.MachinePurposeId === '84be5006-c3d1-4a82-9c6d-8ee1d64dd298';

    this.radiationForm.patchValue({
      ownerName: machine.OwnerName,
      ownerAddress: machine.OwnerAddress,
      ownerPhone: machine.OwnerPhone,
      machineTypeId: machine.MachineTypeId,
      machineTypeOther: machine.MachineTypeOther,
      machinePurposeId: machine.MachinePurposeId,
      machinePurposeOther: machine.MachinePurposeOther,
      kvp: machine.Kvp,
      mA: machine.mA,
      fixed: machine.Fixed,
      mobile: machine.Mobile,
      manufacturerAndModel: machine.ManufacturerAndModel,
      serialNumber: machine.SerialNumber || '',
      machineLocation: machine.MachineLocation || ''
    });

    // Disable all form controls when in view mode
    if (this.isViewMode) {
      this.radiationForm.disable();
    }
  }

  onSubmit() {
    if (!this.canEdit) {
      this.matSnackBar.open('No se puede modificar en el estado actual de la transacción', 'OK', {
        duration: 3000
      });
      return;
    }

    if (this.radiationForm.valid) {
      this.isLoading = true;

      const request = this.isEditMode
        ? this.radiationMachineService.editMachine(this.currentMachineId, this.radiationForm.value)
        : this.radiationMachineService.createMachine(this.radiationForm.value);

      request.subscribe({
        next: (response) => {
          console.log(this.isEditMode ? 'Máquina actualizada exitosamente:' : 'Máquina creada exitosamente:', response);
          this.profileService.getById(this.profileId).then((data: any) => {
            this.radiationForm.patchValue({
              ownerName: data.Name + ' ' + data.LastName,
              ownerAddress: data.OtherStreet1,
              ownerPhone: data.Phone1
            });
          });
          if(!this.isEditMode){
            this.machineList.push(response.Data);
          }else{
            const index = this.machineList.findIndex(machine => machine.Id === this.currentMachineId);
            if (index !== -1) {
              this.machineList.splice(index, 1, response.Data);
            }
          }
          this.dataSource.data = [...this.machineList];
          this.save(true);
        },
        error: (error) => {
          console.error(this.isEditMode ? 'Error al actualizar la máquina:' : 'Error al crear la máquina:', error);
        },
        complete: () => {
          this.isLoading = false;
          this.showMachineForm = false;
          this.resetForm();
        }
      });
    }
  }

  closeForm() {
    if (this.showMachineForm) {
      this.showMachineForm = false;
      this.resetForm();
    } else {
      this.showMachineForm = true;
      this.isEditMode = false;
      this.isViewMode = false;
    }
  }

  resetForm() {
    this.radiationForm.reset({
      userProcessRequirementId: this.dynamicFormService.requirement.UserProcessRequirementId,
      fixed: 'No',
      mobile: 'No'
    });
    this.isEditMode = false;
    this.isViewMode = false;
    this.currentMachineId = null;
    
    // Re-enable form controls when resetting
    if (this.radiationForm.disabled) {
      this.radiationForm.enable();
    }
  }

  deleteMachine(machine: any) {
    if (!this.canEdit) {
      this.matSnackBar.open('No se puede eliminar en el estado actual de la transacción', 'OK', {
        duration: 3000
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '300px',
      panelClass: 'custom-dialog-container',
      data: { message: '¿Desea borrar esta máquina?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.radiationMachineService.deleteMachine(machine.Id)
          .subscribe({
            next: (response) => {
              console.log('Máquina eliminada exitosamente:', response);
              const index = this.machineList.findIndex(m => m.Id === machine.Id);
              if (index !== -1) {
                this.machineList.splice(index, 1);
              }
              this.dataSource.data = [...this.machineList];
              this.save(true);
            },
            error: (error) => {
              console.error('Error al eliminar la máquina:', error);
            },
            complete: () => {
              this.isLoading = false;
            }
          });
      }
    });
  }

  getMachineTypeName(typeId: string): string {
    const machineType = this.machineTypes.find(type => type.Id === typeId);
    return machineType ? machineType.Name : typeId;
  }

  getMachinePurposeName(purposeId: string): string {
    const purposeType = this.purposeTypes.find(purpose => purpose.Id === purposeId);
    return purposeType ? purposeType.Name : purposeId;
  }

  save(isSavingMachine?: boolean) {
    if(!isSavingMachine){
      this.isSaving = true;
    }
    let data: any = {"MachineRadiationCount" : this.machineList.length};
    if (this.machineList.length > 0) {
      console.log('Hay máquinas, marcando como completo');
      this.dynamicFormService.isComplete = true;

      this.dynamicFormService
        .save(data)
        .then(() => {
          if (isSavingMachine) {
            this.dynamicFormService.formLoaderIsVisible = true;
          }else{
            this.dynamicFormService.formLoaderIsVisible = false;
          }
          this.dynamicFormService.formCommunicator.next("stop-saving");
          this.transactionService.transactionRefresher.next();
          let snackBarRef = this.matSnackBar.open('Guardado exitosamente', 'OK', {
            duration: 1000
          });
        })
        .catch((err: any) => {
          console.log('ERROR', err)
        });
    } else if (this.machineList.length === 0){
      console.log('No hay máquinas, marcando como incompleto');
      this.dynamicFormService.isComplete = false;
      this.dynamicFormService
        .save(data)
        .then(() => {
          if (isSavingMachine) {
            this.dynamicFormService.formLoaderIsVisible = true;
          } else {
            this.dynamicFormService.formLoaderIsVisible = false;
          }
          this.dynamicFormService.formCommunicator.next("stop-saving");
          this.transactionService.transactionRefresher.next();
          let snackBarRef = this.matSnackBar.open('Guardado exitosamente', 'OK', {
            duration: 1000
          });
        })
        .catch((err: any) => {
          console.log('ERROR', err)
        });
    }
    setTimeout(() => {
      this.isSaving = false;
    }, 2000);
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }
}
